import {
  createListenerMiddleware,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { filter, includes, some, toLower, values } from 'lodash';
import { AppColumn } from '../../models/custom/AppColumn';
import { RootState } from '../store';

interface FilterState {
  columnKey: string;
  selectedItems: string[];
}

interface DateRangeState {
  columnKey: string;
  startDate: Date;
  endDate: Date;
}

type InitialState = {
  /**
   * data to be rendered on table
   */
  tableFilteredData: any[];
  /**
   * data to be rendered on filters
   */
  filterItemsData: any[];
  globalSearch: string;
  immutableData: any[];
  columns: AppColumn[];
  showColumns: string[];
  activeFilters: FilterState[];
  activeDateRanges: DateRangeState[];
  selectedColumns: string[];
  vehicleVioColumns: AppColumn[];
  showVehicleVioColumns: string[];
  ecoDrivingColumns: AppColumn[];
  showEcoDrivingColumns: string[];
};

const initialState: InitialState = {
  globalSearch: '',
  immutableData: [],
  columns: [],
  showColumns: [],
  tableFilteredData: [],
  filterItemsData: [],
  activeFilters: [],
  activeDateRanges: [],
  selectedColumns: [],
  vehicleVioColumns: [],
  showVehicleVioColumns: [],
  ecoDrivingColumns: [],
  showEcoDrivingColumns: [],
};

const applyFilters = (
  data: any[],
  activeFilters: FilterState[],
  activeDateRanges: DateRangeState[]
): any[] => {
  let filteredData = [...data];

  activeFilters.forEach((filter) => {
    filteredData = filteredData.filter((item) =>
      filter.selectedItems.includes(item[filter.columnKey])
    );
  });

  activeDateRanges.forEach((dateRange) => {
    filteredData = filteredData.filter((item) => {
      const itemDate = dayjs(item[dateRange.columnKey]);
      const startDate = dayjs(dateRange.startDate);
      const endDate = dayjs(dateRange.endDate).endOf('day');
      return (
        (itemDate.isAfter(startDate) || itemDate.isSame(startDate, 'day')) &&
        (itemDate.isBefore(endDate) || itemDate.isSame(endDate, 'day'))
      );
    });
  });

  return filteredData;
};

// Create listener middleware
export const listenerMiddleware = createListenerMiddleware();

const tableSlice = createSlice({
  name: 'tableSlice',
  initialState,
  reducers: {
    setImmutableData: (state, action: PayloadAction<any[]>) => {
      state.immutableData = action.payload;
    },
    setColumns: (state, action: PayloadAction<AppColumn[]>) => {
      state.columns = action.payload;
    },
    setShowColumns: (state, action: PayloadAction<any[]>) => {
      state.showColumns = action.payload;
    },
    setVehicleVioColumns: (state, action: PayloadAction<AppColumn[]>) => {
      state.vehicleVioColumns = action.payload;
    },
    setShowVehicleVioColumns: (state, action: PayloadAction<any[]>) => {
      state.showVehicleVioColumns = action.payload;
    },
    setEcoDrivingColumns: (state, action: PayloadAction<AppColumn[]>) => {
      state.ecoDrivingColumns = action.payload;
    },
    setShowEcoDrivingColumns: (state, action: PayloadAction<any[]>) => {
      state.showEcoDrivingColumns = action.payload;
    },
    setSelectedColumns: (state, action: PayloadAction<string[]>) => {
      state.selectedColumns = action.payload;
    },
    setTableFilteredData: (state, action: PayloadAction<any[]>) => {
      state.tableFilteredData = action.payload;
    },
    setFilterItemsData: (state, action: PayloadAction<any[]>) => {
      state.filterItemsData = action.payload;
    },
    setGlobalSearch: (state, action: PayloadAction<string>) => {
      state.globalSearch = action.payload;
      if (action.payload === '') {
        state.tableFilteredData = applyFilters(
          state.immutableData,
          state.activeFilters,
          state.activeDateRanges
        );
        state.filterItemsData = applyFilters(
          state.immutableData,
          [],
          state.activeDateRanges
        );
      } else if (action.payload.trim() !== '') {
        const filteredData = filter(state.immutableData, (item) =>
          some(values(item), (val) =>
            includes(toLower(val.toString()), toLower(action.payload))
          )
        );
        state.tableFilteredData = filteredData;
        state.filterItemsData = filteredData;
      }
    },
    setHandleImmutableDataChanges: (state, action: PayloadAction<any[]>) => {
      state.immutableData = action.payload;
    },
    setColumnFilter: (
      state,
      action: PayloadAction<{ columnKey: string; selectedItems: string[] }>
    ) => {
      const { columnKey, selectedItems } = action.payload;
      state.activeFilters = state.activeFilters.filter(
        (filter) => filter.columnKey !== columnKey
      );
      if (selectedItems.length > 0) {
        state.activeFilters.push({ columnKey, selectedItems });
      }
    },
    setDateRangeFilter: (
      state,
      action: PayloadAction<{
        columnKey: string;
        startDate: Date;
        endDate: Date;
      }>
    ) => {
      const { columnKey, startDate, endDate } = action.payload;
      state.activeDateRanges = state.activeDateRanges.filter(
        (dateRange) => dateRange.columnKey !== columnKey
      );
      if (startDate && endDate) {
        state.activeDateRanges.push({ columnKey, startDate, endDate });
      }
    },
    clearColumnFilter: (state, action: PayloadAction<string>) => {
      const columnKey = action.payload;
      state.activeFilters = state.activeFilters.filter(
        (filter) => filter.columnKey !== columnKey
      );
      state.activeDateRanges = state.activeDateRanges.filter(
        (dateRange) => dateRange.columnKey !== columnKey
      );
    },
    clearDateRange: (state, action: PayloadAction<string>) => {
      state.activeDateRanges = state.activeDateRanges.filter(
        (dateRange) => dateRange.columnKey !== action.payload
      );
    },
  },
});

// Add listeners for state changes
listenerMiddleware.startListening({
  predicate: (action) =>
    [
      tableSlice.actions.setHandleImmutableDataChanges.type,
      tableSlice.actions.setColumnFilter.type,
      tableSlice.actions.setDateRangeFilter.type,
      tableSlice.actions.clearColumnFilter.type,
      tableSlice.actions.clearDateRange.type,
    ].includes(action.type),
  effect: (_, listenerApi) => {
    const state = listenerApi.getState() as RootState;
    const tableState = state.hedefnet.table;

    // Update tableFilteredData
    const newFilteredData = applyFilters(
      tableState.immutableData,
      tableState.activeFilters,
      tableState.activeDateRanges
    );

    // Update filterItemsData
    const newFilterItemsData = applyFilters(
      tableState.immutableData,
      [],
      tableState.activeDateRanges
    );

    listenerApi.dispatch(
      tableSlice.actions.setTableFilteredData(newFilteredData)
    );
    listenerApi.dispatch(
      tableSlice.actions.setFilterItemsData(newFilterItemsData)
    );
  },
});

export default tableSlice.reducer;
export const tableSliceActions = tableSlice.actions;
export const selectTableState = (state: RootState) => state.hedefnet.table;
